/**
 * El visor de fotos, para mirar la evidencia sin salir de la lista.
 *
 * 🚨 Lo pidió Luis: *«entregas: ver la foto o las fotos sin salir de la lista»*.
 *
 * Antes había que abrir el panel de revisión completo, que carga el cumplido
 * entero en un marco. Para la pregunta que la oficina se hace cien veces al día
 * —«¿esta foto sirve o no?»— eso es demasiado camino.
 *
 * ⭐ Y desde aquí mismo se aprueba o se devuelve: mirar y decidir son el mismo
 * gesto, no dos pantallas.
 */

import { useCallback, useEffect, useState } from 'react'
import { pedirConSesion, tokenGuardado, puedeCambiar } from './Entrar.tsx'

interface Pieza {
  id: string
  tipo: string
  creado_at: string
  /**
   * 🚨 Esta hoja NO venía con la entrega: la subió el conductor después.
   *
   * Es el «cumplido más tarde». Luis lo pidió explícito: en la oficina se ve
   * **con las demás hojas del mismo cumplido**, y se distingue que llegó
   * después. Antes ni siquiera aparecía — ver la nota de `ver-evidencia.ts`.
   */
  llego_despues?: boolean
  /** Byte a byte igual a otra que ya salió antes. Ver `ver-evidencia.ts`. */
  repetida?: boolean
}

/** Cómo se le dice a cada cosa, en palabras de la operación. */
const COMO_SE_LLAMA: Record<string, string> = {
  /* Luis lo corrigió hace tiempo: no es «remesón», es la hoja del cumplido
     que el cliente le entrega al conductor. Y puede tener varias — el pie
     del visor dice cuál de cuántas. */
  FOTO_REMESON: 'Hoja del cumplido',
  FIRMA: 'Firma de quien recibió',
  CUMPLIDO_FISICO: 'Cumplido en papel',
  FOTO_CARGA: 'Foto de la carga',
  FOTO_SELLO: 'Foto del sello',
  FOTO_AVERIA: 'Foto de la avería',
  EIR: 'EIR',
  OTRO: 'Otra foto',
}

export function VerFotos({
  entregaId,
  remesa,
  cliente,
  alCerrar,
  alRevisar,
}: {
  entregaId: string
  remesa: string
  cliente: string | null
  alCerrar: () => void
  alRevisar?: () => void
}) {
  const [piezas, setPiezas] = useState<Pieza[] | null>(null)
  const [cual, setCual] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [urls, setUrls] = useState<Record<string, string>>({})

  useEffect(() => {
    void (async () => {
      try {
        const r = await pedirConSesion<{ evidencia: Pieza[] }>(
          `/api/admin/entregas/${entregaId}/evidencia`,
        )
        setPiezas(r.evidencia)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo cargar')
      }
    })()
  }, [entregaId])

  /*
   * 🚨 La imagen se trae CON la sesión puesta y se muestra desde la memoria.
   *
   * Un `<img src="/api/...">` no manda la cabecera de sesión, así que el
   * servidor respondería 401 y saldría el ícono de imagen rota. Es el mismo
   * enredo que ya apareció con el archivo del manifiesto.
   */
  const traer = useCallback(
    async (id: string) => {
      if (urls[id]) return
      try {
        const r = await fetch(`/api/admin/evidencia/${id}`, {
          headers: { Authorization: `Bearer ${tokenGuardado() ?? ''}` },
        })
        if (!r.ok) {
          /*
           * 🚨 «El archivo no está» NO es lo mismo que «no se pudo abrir».
           *
           * Medido el 24-ago-2026 cotejando la base contra el disco: 6 de 157
           * evidencias apuntaban a archivos que no están. La respuesta era un
           * `404 No encontrado` en texto plano y aquí salía «No se pudo abrir
           * la foto» — que suena a problema del momento, invita a reintentar y
           * esconde lo que de verdad pasó.
           *
           * Quien revisa tiene que poder distinguirlo: si el archivo se perdió,
           * lo que toca es **pedirle otra foto al conductor**, no volver a
           * darle clic.
           */
          const dijo = (await r.json().catch(() => null)) as { error?: string } | null
          throw new Error(
            dijo?.error === 'archivo_perdido'
              ? 'Esta foto está registrada pero el archivo no está en el servidor. Hay que pedirle otra al conductor.'
              : 'No se pudo abrir la foto. Vuelve a intentarlo.',
          )
        }
        const blob = await r.blob()
        setUrls((antes) => ({ ...antes, [id]: URL.createObjectURL(blob) }))
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo abrir la foto')
      }
    },
    [urls],
  )

  useEffect(() => {
    const p = piezas?.[cual]
    if (p) void traer(p.id)
  }, [piezas, cual, traer])

  // Las direcciones en memoria se sueltan al cerrar, si no el navegador las
  // guarda hasta que se recargue la página.
  useEffect(() => {
    return () => {
      for (const u of Object.values(urls)) URL.revokeObjectURL(u)
    }
  }, [urls])

  const cuantas = piezas?.length ?? 0

  /*
   * 🚨 «HOJA 2 DE 5» CUENTA HOJAS, NO PIEZAS.
   *
   * Luis lo pidió con esas palabras. Y decía «de 16» sobre 15 hojas, porque
   * contaba TODO lo que trae la entrega — incluida la firma, que no es una
   * hoja. MEDIDO el 24-ago-2026 en la remesa 001088330: 16 piezas, **15 hojas
   * y 1 firma**. Quien revisa para facturar cuenta hojas, y ahí le sobraba una.
   *
   * ⛔ Y al llegar a la firma decía «Firma de quien recibió · 16 de 16», que no
   * significa nada: firma hay una.
   *
   * ⭐ Las dos clases de hoja cuentan JUNTAS —la que tomó al entregar y la que
   * subió después— porque para la oficina son hojas del mismo cumplido, y
   * porque así el visor dice el mismo número que la columna de la lista. Dos
   * pantallas contando distinto lo mismo es lo que ya nos costó una vez.
   *
   * ⛔ Esto NO cambia por dónde se navega: las flechas siguen pasando por todas
   * las piezas. Lo único que cambia es lo que dice el pie.
   */
  const familia = (tipo: string) =>
    tipo === 'FOTO_REMESON' || tipo === 'CUMPLIDO_FISICO' ? 'hoja' : tipo
  const actualAhora = piezas?.[cual]
  const hermanas = actualAhora
    ? (piezas ?? []).filter((p) => familia(p.tipo) === familia(actualAhora.tipo))
    : []
  const cualDeSuClase = actualAhora ? hermanas.indexOf(actualAhora) + 1 : 0

  /**
   * 🔍 ACERCAR PARA LEER LA FIRMA.
   *
   * Luis, 24-ago-2026: «poder acercar para leer la firma y el sello».
   *
   * Quien revisa en la oficina tiene que **leer** el papel: el nombre de quien
   * recibió, el sello, si la firma está o no. Un cumplido de carta entero en
   * una ventana de 740 px deja los renglones en 8 px, y ahí no se lee nada.
   *
   * Se acerca al punto donde hizo clic —no al centro— porque la firma casi
   * siempre está abajo, y acercar al centro obligaría a arrastrar.
   */
  const [acercado, setAcercado] = useState(false)
  const [foco, setFoco] = useState({ x: 50, y: 50 })

  const mover = useCallback(
    (d: number) => {
      setCual((c) => (cuantas === 0 ? 0 : (c + d + cuantas) % cuantas))
      /* Cambiar de hoja vuelve a verla entera: si no, la siguiente aparece
         acercada en un punto que no tiene nada que ver y se ve rota. */
      setAcercado(false)
    },
    [cuantas],
  )

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      /* 🚨 Estando acercado, Escape ALEJA; no cierra. Si cerrara, el que se
         acercó a leer un sello perdería la hoja de un tecleo. */
      if (e.key === 'Escape') {
        if (acercado) setAcercado(false)
        else alCerrar()
      }
      if (e.key === 'ArrowRight') mover(1)
      if (e.key === 'ArrowLeft') mover(-1)
      if (e.key === '+' || e.key === '=') setAcercado(true)
      if (e.key === '-' || e.key === '0') setAcercado(false)
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  }, [alCerrar, mover, acercado])

  const actual = piezas?.[cual]

  return (
    <div className="visor-velo" onClick={alCerrar}>
      <div className="visor" onClick={(e) => e.stopPropagation()}>
        <div className="visor-cab">
          <div>
            <b className="num">{remesa}</b>
            <span>{cliente ?? 'Sin cliente'}</span>
          </div>
          <button onClick={alCerrar} aria-label="Cerrar" className="visor-x">
            &times;
          </button>
        </div>

        <div className="visor-cuerpo">
          {error && <p className="visor-msg">{error}</p>}
          {!error && piezas === null && <p className="visor-msg">Abriendo…</p>}
          {!error && piezas !== null && cuantas === 0 && (
            <p className="visor-msg">Esta entrega no tiene fotos ni firma.</p>
          )}

          {actual && (
            <>
              {cuantas > 1 && (
                <button className="visor-flecha izq" onClick={() => mover(-1)} aria-label="Anterior">
                  ‹
                </button>
              )}
              {urls[actual.id] ? (
                <img
                  src={urls[actual.id]}
                  alt={COMO_SE_LLAMA[actual.tipo] ?? actual.tipo}
                  className={acercado ? 'acercada' : undefined}
                  style={acercado ? { transformOrigin: `${foco.x}% ${foco.y}%` } : undefined}
                  onClick={(e) => {
                    if (acercado) {
                      setAcercado(false)
                      return
                    }
                    /* Dónde tocó, en porcentaje de la imagen: ese punto es el
                       que se queda quieto al acercar. */
                    const caja = e.currentTarget.getBoundingClientRect()
                    setFoco({
                      x: ((e.clientX - caja.left) / caja.width) * 100,
                      y: ((e.clientY - caja.top) / caja.height) * 100,
                    })
                    setAcercado(true)
                  }}
                />
              ) : (
                <p className="visor-msg">Abriendo…</p>
              )}
              {cuantas > 1 && (
                <button className="visor-flecha der" onClick={() => mover(1)} aria-label="Siguiente">
                  ›
                </button>
              )}
            </>
          )}
        </div>

        <div className="visor-pie">
          <span>
            {actual ? COMO_SE_LLAMA[actual.tipo] ?? actual.tipo : ''}
            {hermanas.length > 1 && (
              <b className="num">
                {' '}
                · {cualDeSuClase} de {hermanas.length}
              </b>
            )}
            {/*
              🚨 De dónde salió esta hoja, cuando no salió de la entrega.
              Quien revisa para facturar necesita saber que esta llegó días
              después: es normal —el cliente devuelve la hoja firmada tarde—
              pero explica por qué la entrega estuvo incompleta un tiempo.
            */}
            {actual?.llego_despues && (
              <span className="visor-pista" style={{ color: '#B4690E' }}>
                Llegó después de terminar el viaje
              </span>
            )}
            {/*
              ⛔ Y si es idéntica a otra, se dice. No se esconde ni se borra:
              la evidencia es inmutable. Pero contar páginas iguales como
              hojas distintas infla la prueba, y una prueba inflada vale menos
              que una honesta.
            */}
            {actual?.repetida && (
              <span className="visor-pista" style={{ color: '#B4690E' }}>
                Es la misma imagen de una hoja anterior
              </span>
            )}
            {/*
              🚨 Se DICE que se puede acercar. Un cursor `zoom-in` solo se ve
              cuando ya se está encima de la foto, y quien revisa a las 7 de la
              mañana no descubre solo que la imagen se toca.
            */}
            <span className="visor-pista">
              {acercado
                ? 'Toca otra vez para verla completa'
                : cuantas > 1
                  ? '· Toca la hoja para acercarla · ← → para pasar'
                  : '· Toca la hoja para acercarla'}
            </span>
          </span>
          {/* Mirar y decidir son el mismo gesto: el botón vive aquí. */}
          {alRevisar && puedeCambiar() && (
            <button
              className="btn-buscar"
              onClick={() => {
                alCerrar()
                alRevisar()
              }}
            >
              Revisar esta entrega
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
